import React, { useEffect, useState } from "react";
import Reveal from "@/components/sections/Reveal";
import NewsletterCapture from "@/components/sections/NewsletterCapture";
import { fetchArticles, fetchPodcasts } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function Writing() {
  const [articles, setArticles] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    Promise.all([fetchArticles().catch(() => []), fetchPodcasts().catch(() => [])])
      .then(([a, p]) => {
        if (!active) return;
        setArticles(Array.isArray(a) ? a : []);
        setPodcasts(Array.isArray(p) ? p : []);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  return (
    <div>
      {/* HERO */}
      <section
        className="rk-section"
        style={{ paddingTop: "calc(var(--rk-section-py) + 60px)" }}
      >
        <div className="rk-container-text">
          <Reveal>
            <div className="rk-eyebrow">Writing</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="rk-heading-page mt-5">The Architecture Letter.</h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 rk-body" style={{ color: "var(--rk-ink)" }}>
              A bi-weekly dispatch for founders and operators — on the decisions
              that determine whether your technology scales. Practitioner
              writing. No motivational preamble. No recycled frameworks.
              Written personally by Rakia Finley.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SUBSCRIBE */}
      <section
        className="rk-section-tight"
        style={{ backgroundColor: "var(--rk-surface)" }}
      >
        <NewsletterCapture source="writing" />
      </section>

      {/* ARTICLES */}
      <section className="rk-section">
        <div className="rk-container-wide">
          <Reveal>
            <div className="rk-eyebrow">Article index</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="rk-heading-section mt-5">Recent writing.</h2>
          </Reveal>

          {loading ? (
            <p className="mt-10 rk-caption">Loading…</p>
          ) : articles.length === 0 ? (
            <Reveal delay={0.1}>
              <p className="mt-10 rk-body" style={{ color: "var(--rk-muted)", maxWidth: 600 }}>
                First issue coming soon. Subscribe above to receive it directly.
              </p>
            </Reveal>
          ) : (
            <ul className="mt-12">
              {articles.map((a, i) => (
                <Reveal key={a.id} delay={i * 0.03}>
                  <li
                    className="py-7"
                    style={{
                      borderTop: "1px solid var(--rk-border)",
                      borderBottom:
                        i === articles.length - 1 ? "1px solid var(--rk-border)" : "none",
                    }}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-baseline">
                      <div
                        className="md:col-span-2 rk-caption"
                        style={{ color: "var(--rk-muted)" }}
                      >
                        {formatDate(a.published_date || a.created_at)}
                      </div>
                      <div className="md:col-span-10">
                        {a.url ? (
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: "none", color: "inherit" }}
                            className="group"
                          >
                            <h3
                              style={{
                                fontFamily: "var(--font-heading)",
                                fontSize: "clamp(22px, 2.3vw, 30px)",
                                lineHeight: 1.2,
                                letterSpacing: "-0.01em",
                              }}
                              className="group-hover:[border-bottom-color:var(--rk-copper)]"
                            >
                              {a.title}
                            </h3>
                          </a>
                        ) : (
                          <h3
                            style={{
                              fontFamily: "var(--font-heading)",
                              fontSize: "clamp(22px, 2.3vw, 30px)",
                              lineHeight: 1.2,
                              letterSpacing: "-0.01em",
                            }}
                          >
                            {a.title}
                          </h3>
                        )}
                        {a.summary && (
                          <p
                            className="mt-3 rk-body"
                            style={{ color: "var(--rk-muted)", maxWidth: 720 }}
                          >
                            {a.summary}
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* PODCASTS */}
      <section
        className="rk-section"
        style={{ backgroundColor: "var(--rk-surface)" }}
      >
        <div className="rk-container-wide">
          <Reveal>
            <div className="rk-eyebrow">Podcast appearances</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="rk-heading-section mt-5">On the record.</h2>
          </Reveal>
          {loading ? (
            <p className="mt-10 rk-caption">Loading…</p>
          ) : podcasts.length === 0 ? (
            <Reveal delay={0.1}>
              <p className="mt-10 rk-body" style={{ color: "var(--rk-muted)", maxWidth: 600 }}>
                Appearances will be listed here as they air.
              </p>
            </Reveal>
          ) : (
            <ul className="mt-12">
              {podcasts.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.03}>
                  <li
                    className="py-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-baseline"
                    style={{
                      borderTop: "1px solid var(--rk-border)",
                      borderBottom:
                        i === podcasts.length - 1 ? "1px solid var(--rk-border)" : "none",
                    }}
                  >
                    <div
                      className="md:col-span-3 rk-caption"
                      style={{ color: "var(--rk-muted)" }}
                    >
                      {formatDate(p.date || p.created_at)} · {p.show_name}
                    </div>
                    <div className="md:col-span-9">
                      {p.url ? (
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noreferrer"
                          className="rk-link"
                          style={{
                            fontFamily: "var(--font-heading)",
                            fontSize: "clamp(20px, 2vw, 26px)",
                            lineHeight: 1.25,
                          }}
                        >
                          {p.episode_title}
                        </a>
                      ) : (
                        <span
                          style={{
                            fontFamily: "var(--font-heading)",
                            fontSize: "clamp(20px, 2vw, 26px)",
                            lineHeight: 1.25,
                          }}
                        >
                          {p.episode_title}
                        </span>
                      )}
                      {p.description && (
                        <p className="mt-2 rk-caption">{p.description}</p>
                      )}
                    </div>
                  </li>
                </Reveal>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
